import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ApiError,
  type AppearanceFieldErrors,
  type AssistantConfig,
  type AssistantResponse,
  type HexColor,
  type OperatorBranding,
  type WidgetAppearance,
  assistantApi,
} from "./api.ts";
import { mergeOverDefaults } from "./savedConfig.mjs";

const DEFAULT_WIDGET_APPEARANCE = Object.freeze({
  version: 1 as const,
  launcher: Object.freeze({
    icon: "eye" as const,
    shape: "circle" as const,
    size: "medium" as const,
    label: "Open chat",
    background: "#3FCF8E" as HexColor,
    foreground: "#001A0B" as HexColor,
    logo_data_url: null,
  }),
  avatar: Object.freeze({
    mode: "eye" as const,
    image_data_url: null,
    initials: "",
  }),
  colors: Object.freeze({
    accent: "#3FCF8E" as HexColor,
    on_accent: "#001A0B" as HexColor,
    header: "#04120C" as HexColor,
    header_text: "#F4FBF7" as HexColor,
    user_bubble: "#164C34" as HexColor,
    user_text: "#F4FBF7" as HexColor,
    // Mirrors tools/appearance_config.py's SURFACE_COLOR_DEFAULTS -- the
    // widget's own dark theme, inert unless surface_mode is "custom".
    surface_mode: "auto" as const,
    surface: "#04120C" as HexColor,
    surface_2: "#06160F" as HexColor,
    text: "#F4FBF7" as HexColor,
    muted: "#6E847A" as HexColor,
    border: "#1B2620" as HexColor,
    agent_bubble: "#111D18" as HexColor,
    agent_text: "#F4FBF7" as HexColor,
  }),
  panel: Object.freeze({
    size: "standard" as const,
    shape: "rounded" as const,
  }),
  suggestions: Object.freeze([] as string[]),
  placement: Object.freeze({
    position: "br" as const,
    offset_x: 20,
    offset_y: 20,
    z_index: 2_147_483_000,
  }),
});

const DEFAULT_OPERATOR_BRANDING = Object.freeze({
  version: 1 as const,
  display_name: "Satatam",
  accent: "#3FCF8E" as HexColor,
  on_accent: "#001A0B" as HexColor,
  logo_data_url: null,
  console_surface_tint: false,
  console_theme_mode: "default" as const,
  console_background: "#04120C" as HexColor,
  console_foreground: "#F4FBF7" as HexColor,
  console_light_background: "#F4F7F3" as HexColor,
  console_light_foreground: "#07140C" as HexColor,
});

export function defaultWidgetAppearance(): WidgetAppearance {
  return {
    version: DEFAULT_WIDGET_APPEARANCE.version,
    launcher: { ...DEFAULT_WIDGET_APPEARANCE.launcher },
    avatar: { ...DEFAULT_WIDGET_APPEARANCE.avatar },
    colors: { ...DEFAULT_WIDGET_APPEARANCE.colors },
    panel: { ...DEFAULT_WIDGET_APPEARANCE.panel },
    suggestions: [...DEFAULT_WIDGET_APPEARANCE.suggestions],
    placement: { ...DEFAULT_WIDGET_APPEARANCE.placement },
  };
}

export function defaultOperatorBranding(): OperatorBranding {
  return { ...DEFAULT_OPERATOR_BRANDING };
}

export type AppearanceStatus = "loading" | "ready" | "saving" | "saved" | "error";

export interface AppearanceIdentity {
  agentName: string;
  companyName: string;
}

export interface AppearanceSettings {
  widget_appearance: WidgetAppearance;
  operator_branding: OperatorBranding;
}

interface AppearanceContextValue {
  widgetAppearance: WidgetAppearance;
  operatorBranding: OperatorBranding;
  identity: AppearanceIdentity;
  identityReady: boolean;
  status: AppearanceStatus;
  fieldErrors: AppearanceFieldErrors;
  save: (settings: AppearanceSettings) => Promise<AppearanceSettings>;
  reload: () => Promise<void>;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

/** One in-flight `GET /api/assistant`, shared by everyone who asks during it.
 *
 * Two providers read this same row on every console mount -- `AppearanceProvider`
 * for the tenant identity and `AppearanceStudioProvider` for the studio extras
 * -- so every operator page made two identical requests and threw one answer
 * away. StrictMode's double-invoke is the same bug again: the old mount effect
 * aborted its own first request and the remount issued another.
 *
 * This is a COALESCER, not a cache. The promise is dropped the moment it
 * settles, so the next caller after that gets a fresh read: a save, a retry and
 * a tenant switch all still reach the server, and there is no invalidation rule
 * to get wrong. A cache here would show somebody another tenant's brand.
 *
 * It deliberately takes no `AbortSignal`: one caller's unmount must not cancel
 * a request another caller is still waiting on. Staleness is already handled by
 * each provider's own `generation` guard, which drops a late response.
 */
let inFlightAssistantConfig: Promise<AssistantResponse> | null = null;

export function sharedAssistantConfig(): Promise<AssistantResponse> {
  if (inFlightAssistantConfig === null) {
    const request = assistantApi.get();
    inFlightAssistantConfig = request;
    const release = () => {
      // Only clear OUR request: a later caller may already have installed the
      // next one by the time this settles.
      if (inFlightAssistantConfig === request) inFlightAssistantConfig = null;
    };
    request.then(release, release);
  }
  return inFlightAssistantConfig;
}

/** Drops the shared read, so the next caller starts a new one.
 *
 * The slot has no tenant key and this is a single page: signing out is a React
 * state change, not a document load, so an in-flight GET outlives it. Without
 * this, a tenant signing in while the previous tenant's read is still stalled
 * would join that request and be painted the previous tenant's brand, and a
 * save from that state would write it into their row.
 *
 * Abandoning a request mid-flight is safe because `release` only clears the
 * slot when it still holds THAT request: the dropped promise settling later
 * reaches whoever was already awaiting it and cannot disturb the read that
 * replaced it. Called from `setSession` in auth-context, which every session
 * change goes through -- sign-in, the callback, sign-out, expiry and the
 * `authApi.me` identity mismatch. Do not move it back to `signOut` alone:
 * that leaves the mismatch and 401/403 transitions uncovered, and
 * `app/test/shared-assistant-config-reset.test.mjs` is the pin that says so.
 * Not from a provider's cleanup either -- an unmount is not a tenant change.
 *
 * It is also the WRITE BARRIER. Every write to this row that is followed by a
 * reload calls it the moment the write resolves and before the reload starts:
 * `save()` here, in aria-context and in appearance-studio-context, and
 * `VersionHistorySection.restore()` in AriaPage. A read that began before the
 * write carries the pre-write row, and the coalescer cannot tell -- it knows
 * only that a GET is outstanding. `app/test/assistant-config-write-barrier.test.mjs`
 * pins all four, and the behaviour is pinned by the barrier stage in
 * `app/test/aria-versions-browser.tsx`.
 */
export function resetSharedAssistantConfig(): void {
  inFlightAssistantConfig = null;
}

function normalizedConfig(response: Awaited<ReturnType<typeof assistantApi.get>>): AssistantConfig {
  const config = response.result?.config;
  if (
    !response.ok
    || !config
    || config.widget_appearance?.version !== 1
    || config.operator_branding?.version !== 1
  ) {
    throw new Error(response.error || "Appearance settings were unavailable.");
  }
  // Merged, not taken whole: the API can be a release behind this bundle, and
  // a field it does not send yet reaches render as `undefined`. See
  // mergeOverDefaults -- `avatar.initials` took the whole page down that way.
  return {
    ...config,
    widget_appearance: mergeOverDefaults(defaultWidgetAppearance(), config.widget_appearance),
    operator_branding: mergeOverDefaults(defaultOperatorBranding(), config.operator_branding),
  };
}

function apiFieldErrors(error: unknown): AppearanceFieldErrors {
  if (!(error instanceof ApiError) || typeof error.body !== "object" || error.body === null) {
    return {};
  }
  const body = error.body as Record<string, unknown>;
  const raw = body.field_errors;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};

  return Object.fromEntries(
    Object.entries(raw).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [widgetAppearance, setWidgetAppearance] = useState(defaultWidgetAppearance);
  const [operatorBranding, setOperatorBranding] = useState(defaultOperatorBranding);
  // Empty, not a product name of ours. Every consumer of this identity
  // renders it into the CUSTOMER's workspace or their own widget preview, so
  // a placeholder name seeded here would show a tenant a name they never
  // chose while the real one is still loading. Consumers fall back the way
  // the shipped widget does.
  const [identity, setIdentity] = useState<AppearanceIdentity>({
    agentName: "",
    companyName: "",
  });
  const [identityReady, setIdentityReady] = useState(false);
  const [status, setStatus] = useState<AppearanceStatus>("loading");
  const [fieldErrors, setFieldErrors] = useState<AppearanceFieldErrors>({});
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    setStatus("loading");
    setFieldErrors({});

    try {
      const config = normalizedConfig(await sharedAssistantConfig());
      if (requestController.signal.aborted || generation.current !== requestGeneration) return;
      setWidgetAppearance(config.widget_appearance);
      setOperatorBranding(config.operator_branding);
      setIdentity({
        agentName: config.agent_name,
        companyName: config.company,
      });
      setIdentityReady(true);
      setStatus("ready");
    } catch {
      if (requestController.signal.aborted || generation.current !== requestGeneration) return;
      // Defaults or the last known-good state remain intact on every failure.
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void reload();
    /* No cleanup. The read is shared (`sharedAssistantConfig`), so aborting it
       on unmount would cancel a request the OTHER provider is still waiting on
       -- and under StrictMode the cleanup would abort the very request the
       remount then has to issue again, which is the double GET this change
       removes. A response that lands after unmount is dropped by the
       `generation` guard inside `reload`, and setting state on an unmounted
       component is a no-op in React 18+. `save()` keeps its own controller:
       that request has exactly one owner. */
  }, [reload]);

  const save = useCallback(async (settings: AppearanceSettings) => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    setStatus("saving");
    setFieldErrors({});

    try {
      const written = await assistantApi.update({
        widget_appearance: settings.widget_appearance,
        operator_branding: settings.operator_branding,
      }, requestController.signal);
      // WRITE BARRIER. The row on the server has just changed, and any GET that
      // was already in flight when it did carries the row as it was BEFORE.
      // The coalescer is keyed on nothing but "is a read outstanding", so the
      // next reload() -- this provider's, the studio's, the persona's -- would
      // join that pre-write read and paint the value just replaced. Dropping
      // the slot here costs at most one extra GET and makes "a read that starts
      // after a write sees the write" true by construction.
      resetSharedAssistantConfig();
      const config = normalizedConfig(written);
      const normalized = {
        widget_appearance: config.widget_appearance,
        operator_branding: config.operator_branding,
      };
      if (requestController.signal.aborted || generation.current !== requestGeneration) {
        return normalized;
      }
      setWidgetAppearance(normalized.widget_appearance);
      setOperatorBranding(normalized.operator_branding);
      setIdentity({
        agentName: config.agent_name,
        companyName: config.company,
      });
      setIdentityReady(true);
      setStatus("saved");
      return normalized;
    } catch (error) {
      if (!requestController.signal.aborted && generation.current === requestGeneration) {
        setFieldErrors(apiFieldErrors(error));
        setStatus("error");
      }
      throw error;
    }
  }, []);

  const value = useMemo<AppearanceContextValue>(() => ({
    widgetAppearance,
    operatorBranding,
    identity,
    identityReady,
    status,
    fieldErrors,
    save,
    reload,
  }), [widgetAppearance, operatorBranding, identity, identityReady, status, fieldErrors, save, reload]);

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance(): AppearanceContextValue {
  const value = useContext(AppearanceContext);
  if (!value) {
    throw new Error("useAppearance must be used inside <AppearanceProvider>");
  }
  return value;
}
